import { DownOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Alert, Button, Drawer, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { InspectionDeviceReadiness, InspectionParameterDeviceReadiness } from '../../modules/inspection-appointments/services/inspection-appointmentService';
import { getMissingPlansInRange, loadDeviceReadiness } from './planDeviceReadiness';
import type { DeviceReadinessPeriod, DeviceReadinessScope } from './planDeviceReadiness';

const { Text } = Typography;

const renderInstruments = (
  instruments: InspectionDeviceReadiness['availableInstruments'],
  color: 'green' | 'red',
  emptyText: string,
) => instruments.length ? (
  <Space size={[0, 6]} wrap>
    {instruments.map((instrument) => (
      <Tag key={instrument.id} color={color} style={{ whiteSpace: 'normal' }}>
        {instrument.name}
      </Tag>
    ))}
  </Space>
) : <Text type="secondary">{emptyText}</Text>;

const createColumns = (scope: DeviceReadinessScope): ColumnsType<InspectionDeviceReadiness> => [
  {
    title: 'Plan Tarihi',
    key: 'date',
    width: 170,
    render: (_, plan) => (
      <div>
        <div>{dayjs(plan.startDate).format('DD.MM.YYYY HH:mm')}</div>
        <Text type="secondary">{dayjs(plan.endDate).format('DD.MM.YYYY HH:mm')}</Text>
      </div>
    ),
  },
  {
    title: 'Firma / Plan',
    key: 'company',
    width: 220,
    render: (_, plan) => (
      <div>
        <div style={{ fontWeight: 600 }}>{plan.companyName || 'Firma bilgisi bulunamadı'}</div>
        {plan.proposalId != null && (
          <Link to={`${scope === 'me' ? '/inspector/planning/detail' : '/planning/detail'}/${plan.proposalId}`}>
            {plan.proposalNumber || `Plan #${plan.proposalId}`}
          </Link>
        )}
      </div>
    ),
  },
  {
    title: 'P.K.M. Uzmanı',
    dataIndex: 'inspectorName',
    key: 'inspector',
    width: 180,
    render: (name: string, plan) => name || `Uzman #${plan.inspectorId}`,
  },
  {
    title: 'Mevcut Cihazlar',
    dataIndex: 'availableInstruments',
    key: 'available',
    render: (instruments) => renderInstruments(instruments, 'green', 'Mevcut cihaz yok'),
  },
  {
    title: 'Eksik Cihazlar',
    dataIndex: 'missingInstruments',
    key: 'missing',
    render: (instruments) => renderInstruments(instruments, 'red', 'Eksik cihaz yok'),
  },
];

const parameterColumns: ColumnsType<InspectionParameterDeviceReadiness> = [
  { title: 'Parametre', dataIndex: 'attributeName', key: 'parameter', width: 260 },
  {
    title: 'Mevcut Cihazlar',
    dataIndex: 'availableInstruments',
    key: 'available',
    render: (instruments, parameter) => renderInstruments(instruments, 'green',
      parameter.missingInstruments.length ? 'Mevcut cihaz yok' : 'Cihaz gerekmiyor'),
  },
  {
    title: 'Eksik Cihazlar',
    dataIndex: 'missingInstruments',
    key: 'missing',
    render: (instruments, parameter) => renderInstruments(instruments, 'red',
      parameter.availableInstruments.length ? 'Eksik cihaz yok' : '—'),
  },
];

interface PlanDeviceReadinessDrawerProps {
  scope: DeviceReadinessScope;
  periodDays: DeviceReadinessPeriod;
  today: string;
  initialPlans: InspectionDeviceReadiness[];
  onClose: () => void;
}

const PlanDeviceReadinessDrawer = ({ scope, periodDays, today, initialPlans, onClose }: PlanDeviceReadinessDrawerProps) => {
  const [startDate, setStartDate] = useState(today);
  const [plans, setPlans] = useState<InspectionDeviceReadiness[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  const endDate = dayjs(startDate).add(periodDays - 1, 'day').format('YYYY-MM-DD');
  const title = `Planların Cihaz İhtiyaçları – ${periodDays === 7 ? 'Haftalık' : 'Aylık'}`;
  const columns = useMemo(() => createColumns(scope)
    .filter((column) => scope === 'all' || column.key !== 'inspector'), [scope]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    setPlans([]);
    setExpandedRowKeys([]);
    if (startDate === today && retry === 0) {
      setPlans(initialPlans);
      setLoading(false);
      return;
    }
    loadDeviceReadiness(scope, startDate, endDate)
      .then((data) => {
        if (!cancelled) setPlans(data);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [scope, startDate, endDate, retry, today, initialPlans]);

  const missingPlans = useMemo(
    () => getMissingPlansInRange(plans, startDate, periodDays),
    [plans, startDate, periodDays],
  );

  return (
    <Drawer
      title={title}
      aria-label={title}
      open
      width="100vw"
      placement="right"
      closable={false}
      onClose={onClose}
      extra={<Button onClick={onClose}>Kapat</Button>}
      style={{ width: '100%', height: '100%' }}
      styles={{ body: { padding: 20 } }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 16 }}>
        <div>
          <Text type="secondary">
            {scope === 'me'
              ? 'Yalnızca planlarınızdaki eksik cihazlar gösterilir. Cihaz ihtiyaçları, onaylı cihaz atamalarınızla karşılaştırılır.'
              : 'Yalnızca eksik cihazı bulunan planlar gösterilir. Cihaz ihtiyaçları, P.K.M. uzmanının onaylı cihaz atamalarıyla karşılaştırılır.'}
          </Text>
        </div>
        <Space wrap>
          <Button
            icon={<LeftOutlined />}
            aria-label={`Önceki ${periodDays} gün`}
            title={`Önceki ${periodDays} gün`}
            onClick={() => setStartDate((date) => dayjs(date).subtract(periodDays, 'day').format('YYYY-MM-DD'))}
          />
          <Text strong>{dayjs(startDate).format('DD.MM.YYYY')} – {dayjs(endDate).format('DD.MM.YYYY')}</Text>
          <Button
            icon={<RightOutlined />}
            aria-label={`Sonraki ${periodDays} gün`}
            title={`Sonraki ${periodDays} gün`}
            onClick={() => setStartDate((date) => dayjs(date).add(periodDays, 'day').format('YYYY-MM-DD'))}
          />
          <Button onClick={() => setStartDate(today)}>Bugün</Button>
        </Space>
      </div>

      {!loading && !error && (
        <div style={{ marginBottom: 16 }}>
          <Tag color={missingPlans.length ? 'red' : 'green'}>{missingPlans.length} planda cihaz eksik</Tag>
        </div>
      )}

      {error ? (
        <Alert
          type="error"
          showIcon
          message="Planların cihaz ihtiyaçları yüklenemedi."
          action={<Button size="small" onClick={() => setRetry((value) => value + 1)}>Tekrar Dene</Button>}
        />
      ) : (
        <Table<InspectionDeviceReadiness>
          rowKey="appointmentId"
          size="small"
          loading={loading}
          columns={columns}
          dataSource={missingPlans}
          scroll={{ x: scope === 'me' ? 820 : 1000 }}
          expandable={{
            expandedRowKeys,
            onExpandedRowsChange: (keys) => setExpandedRowKeys([...keys]),
            rowExpandable: (plan) => plan.parameters.length > 0,
            expandIcon: ({ expanded, onExpand, record }) => record.parameters.length > 0 ? (
              <Button
                type="text"
                size="small"
                icon={expanded ? <DownOutlined /> : <RightOutlined />}
                aria-expanded={expanded}
                aria-label={`Parametre ayrıntılarını ${expanded ? 'gizle' : 'göster'}`}
                onClick={(event) => onExpand(record, event)}
              />
            ) : null,
            expandedRowRender: (plan) => (
              <div role="region" aria-label="Parametrelerin cihaz ihtiyaçları">
                <Table<InspectionParameterDeviceReadiness>
                  rowKey="attributeId"
                  size="small"
                  columns={parameterColumns}
                  dataSource={plan.parameters}
                  pagination={false}
                  scroll={{ x: 700 }}
                />
              </div>
            ),
          }}
          pagination={false}
          locale={{ emptyText: loading ? 'Planlar yükleniyor...'
            : 'Seçilen tarih aralığında eksik cihazı olan plan bulunamadı.' }}
        />
      )}
    </Drawer>
  );
};

export default PlanDeviceReadinessDrawer;
